import { CheckIcon } from '@heroicons/react/24/outline';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { stringify } from 'query-string';
import React, { useState, useEffect } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import useRewardful from '@dashboard/hooks/useRewardful';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { stripePublicKey } from '@dashboard/services/env';
import { formatPrice } from '@dashboard/services/formatting';
import getCouponDiscount from '@dashboard/services/getCouponDiscount';
import { getPlan } from '@dashboard/services/plans';
import { fetcher } from '@dashboard/services/request';

import Button from '@dashboard/components/Button';
import Link from '@dashboard/components/Link';
import { InfoTooltip } from '@dashboard/components/Tooltip';

import { SubscriptionButton } from './BillingSettings/scenes/Plan/PlanOverview';
import { UpgradeForm } from './BillingSettings/scenes/Plan/Upgrade';

const PlansPage = () => {
  const { isTrialExpired, isCanceled, plan: sellerPlan, seller } = useAuth();
  const history = useHistory();

  const { billing_period: billingPeriodQuery, ...rest } = useUrlQuery();

  const [billingPeriod, setBillingPeriod] = useState(billingPeriodQuery || 'year');
  const { upgradeFlow = 'upgrade-page' } = useUrlQuery();

  function handleSetBillingPeriod(period) {
    history.replace(
      `/upgrade?${stringify({
        billing_period: period,
        ...rest,
      })}`,
    );
  }

  useEffect(() => {
    if (billingPeriodQuery && billingPeriodQuery !== billingPeriod) {
      setBillingPeriod(billingPeriodQuery);
    }
  }, [billingPeriodQuery]);

  const currentPlan = getPlan(sellerPlan.handle, sellerPlan.version) || {};
  const isCurrentBillingPeriod = billingPeriod === seller.planBillingPeriod;

  const hasActiveSubscription = seller?.subscriptionStatus === 'active';

  return (
    <div className="container mx-auto px-4 py-8">
      <div>
        <div className="flex items-center justify-between mb-8">
          {!isTrialExpired && !isCanceled && (
            <h1 className="text-2xl font-bold">Select your plan</h1>
          )}

          {isTrialExpired && (
            <div>
              <h1 className="text-2xl font-bold">Your trial has expired</h1>
              <p className="text-lg">Upgrade today to start accepting payments.</p>
            </div>
          )}

          {isCanceled && (
            <div>
              <h1 className="text-2xl font-bold">Your subscription has ended</h1>
              <p className="text-lg">Upgrade today to continue accepting payments.</p>
            </div>
          )}

          <div className="">
            <div className="flex justify-center items-center">
              <fieldset className="grid grid-cols-2 gap-x-1 rounded-full p-2 text-center leading-5 ring-1 ring-inset ring-gray-200 relative bg-white">
                <legend className="sr-only">Payment frequency</legend>
                <label
                  className={`cursor-pointer rounded-full py-1 px-4 font-medium ${
                    billingPeriod === 'month' ? 'bg-chocolate-1000 text-white' : 'text-gray-500'
                  }`}
                >
                  <input
                    type="radio"
                    name="frequency"
                    value="month"
                    className="sr-only"
                    checked={billingPeriod === 'month'}
                    onChange={() => handleSetBillingPeriod('month')}
                  />
                  <span>Monthly</span>
                </label>

                <label
                  className={`cursor-pointer rounded-full py-1 px-4 font-medium ${
                    billingPeriod === 'year' ? 'bg-chocolate-1000 text-white' : 'text-gray-500'
                  }`}
                >
                  <input
                    type="radio"
                    name="frequency"
                    value="year"
                    className="sr-only"
                    checked={billingPeriod === 'year'}
                    onChange={() => handleSetBillingPeriod('year')}
                  />
                  <span>Yearly - Save 30%</span>
                </label>
              </fieldset>
            </div>
          </div>
        </div>

        <div className="">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-8 md:m-auto pb-8">
            <div
              className={clsx('shadow p-8 relative rounded-lg bg-white', {
                'border-2 border-chocolate-1000':
                  hasActiveSubscription && currentPlan.handle === 'launch',
                'border border-gray-100': !hasActiveSubscription || currentPlan.handle !== 'launch',
              })}
            >
              <h3 className="flex items-center text-lg font-semibold">Launch</h3>
              <span className=" inline-block text-base pb-8 text-gray-600">
                Up to $3k sales per month
                <InfoTooltip>
                  We'll never stop you from taking payments. Check out our{' '}
                  <Link href="/help/articles/pricing-fees" target="blank" variant="light">
                    pricing guide
                  </Link>{' '}
                  for more information
                </InfoTooltip>
              </span>

              <h3 className="text-5xl pb-4">
                <span className="font-semibold">{billingPeriod === 'month' ? '$29' : '$24'}</span>{' '}
                <span className="text-xl font-light text-gray-400 -ml-3">/ month</span>
                <span className="mt-2 text-base font-light text-gray-400 block">
                  {billingPeriod === 'year' ? '($290 billed annually)' : ''}
                </span>
              </h3>

              <div className="mb-8">
                {currentPlan.handle === 'launch' &&
                isCurrentBillingPeriod &&
                hasActiveSubscription ? (
                  <div className="py-4 text-center rounded border border-gray-200 text-gray-600">
                    Your current plan
                  </div>
                ) : (
                  <SubscriptionButton
                    plan={getPlan('launch')}
                    billingPeriod={billingPeriod}
                    setSelectedPlanHandle={() => {}}
                    upgradePath="/upgrade/subscribe"
                    upgradeFlow={upgradeFlow}
                  />
                )}
              </div>

              <h4 className="text-sm font-normal text-gray-600 pb-2">
                <p>
                  Includes <span className="font-semibold text-black">99% of features</span>, plus:
                </p>
              </h4>

              <ul className="flex flex-col gap-y-1 text-base text-gray-600">
                <li className="flex items-center gap-x-1">
                  <CheckIcon className="shrink-0 w-4 h-4 text-chocolate-600" /> 0% transaction fees{' '}
                  <InfoTooltip>You'll only pay Stripe payment processing fees</InfoTooltip>
                </li>
                <li className="flex items-center gap-x-1">
                  <CheckIcon className="shrink-0 w-4 h-4 text-chocolate-600" />
                  Standard support
                  <InfoTooltip>Chat & email support</InfoTooltip>
                </li>
              </ul>
            </div>

            <div
              className={clsx('shadow p-8 relative rounded-lg bg-white', {
                'border-2 border-chocolate-1000':
                  hasActiveSubscription && currentPlan.handle === 'grow',
                'border border-gray-100': !hasActiveSubscription || currentPlan.handle !== 'grow',
              })}
            >
              <h3 className="flex items-center text-lg font-semibold">Grow</h3>
              <span className=" inline-block text-base pb-8 text-gray-600">
                Up to $10k sales per month{' '}
                <InfoTooltip>
                  We'll never stop you from taking payments. Check out our{' '}
                  <Link href="/help/articles/pricing-fees" target="blank" variant="light">
                    pricing guide
                  </Link>{' '}
                  for more information
                </InfoTooltip>
              </span>
              <h3 className="text-5xl pb-4">
                <span className="font-semibold">{billingPeriod === 'month' ? '$99' : '$83'}</span>{' '}
                <span className="text-xl font-light text-gray-400 -ml-3">/ month</span>
                <span className="mt-2 text-base font-light text-gray-400 block">
                  {billingPeriod === 'year' ? '($990 billed annually)' : ''}
                </span>
              </h3>

              <div className="mb-6">
                {currentPlan.handle === 'grow' &&
                isCurrentBillingPeriod &&
                hasActiveSubscription ? (
                  <div className="py-4 text-center rounded border border-gray-200 text-gray-600">
                    Your current plan
                  </div>
                ) : (
                  <SubscriptionButton
                    plan={getPlan('grow')}
                    billingPeriod={billingPeriod}
                    setSelectedPlanHandle={() => {}}
                    upgradePath="/upgrade/subscribe"
                    upgradeFlow={upgradeFlow}
                  />
                )}
              </div>

              <h4 className="text-sm font-normal text-gray-600 pb-2">
                <p>
                  Everything in <span className="font-semibold text-black">Launch</span>, plus:
                </p>
              </h4>

              <ul className="flex flex-col gap-y-1 text-base text-gray-600">
                <li className="flex items-center gap-x-1">
                  <CheckIcon className="shrink-0 w-4 h-4 text-chocolate-600" /> 0% transaction fees{' '}
                  <InfoTooltip>You'll only pay Stripe payment processing fees</InfoTooltip>
                </li>
                <li className="flex items-center gap-x-1">
                  <CheckIcon className="shrink-0 w-4 h-4 text-chocolate-600" />
                  Premium support
                  <InfoTooltip label="Priority chat & email support" />
                </li>
                <li className="flex items-center gap-x-1">
                  <CheckIcon className="shrink-0 w-4 h-4 text-chocolate-600" /> Custom domain{' '}
                  <InfoTooltip>
                    Add your domain name to your checkout, event, and form pages
                  </InfoTooltip>
                </li>
                <li className="flex items-center gap-x-1">
                  <CheckIcon className="shrink-0 w-4 h-4 text-chocolate-600" /> Custom email domain{' '}
                  <InfoTooltip>Send confirmation emails from your email address</InfoTooltip>
                </li>
              </ul>
            </div>

            <div
              className={clsx('shadow p-8 relative rounded-lg bg-white', {
                'border-2 border-chocolate-1000':
                  hasActiveSubscription && currentPlan.handle === 'scale',
                'border border-gray-100': !hasActiveSubscription || currentPlan.handle !== 'scale',
              })}
            >
              <h3 className="flex items-center text-lg font-semibold">Scale</h3>
              <span className=" inline-block text-base pb-8 text-gray-600">
                Up to $30k sales per month{' '}
                <InfoTooltip>
                  We'll never stop you from taking payments. Check out our{' '}
                  <Link href="/help/articles/pricing-fees" target="blank" variant="light">
                    pricing guide
                  </Link>{' '}
                  for more information
                </InfoTooltip>
              </span>
              <h3 className="text-5xl pb-4">
                <span className="font-semibold">{billingPeriod === 'month' ? '$239' : '$199'}</span>{' '}
                <span className="text-xl font-light text-gray-400 -ml-3">/ month</span>
                <span className="mt-2 text-base font-light text-gray-400 block">
                  {billingPeriod === 'year' ? '($2390 billed annually)' : ''}
                </span>
              </h3>

              <div className="mb-6">
                {currentPlan.handle === 'scale' &&
                isCurrentBillingPeriod &&
                hasActiveSubscription ? (
                  <Button appearance="secondary" size="full" disabled>
                    Your current plan
                  </Button>
                ) : (
                  <SubscriptionButton
                    plan={getPlan('scale')}
                    billingPeriod={billingPeriod}
                    setSelectedPlanHandle={() => {}}
                    upgradePath="/upgrade/subscribe"
                    upgradeFlow={upgradeFlow}
                  />
                )}
              </div>

              <h4 className="text-sm font-normal text-gray-600 pb-2">
                <p>
                  Everything in <span className="font-semibold text-black">Grow</span>, plus:
                </p>
              </h4>

              <ul className="flex flex-col gap-y-1 text-base text-gray-600">
                <li className="flex items-center gap-x-1">
                  <CheckIcon className="shrink-0 w-4 h-4 text-chocolate-600" /> 0% transaction fees{' '}
                  <InfoTooltip>You'll only pay Stripe payment processing fees</InfoTooltip>
                </li>
                <li className="flex items-center gap-x-1">
                  <CheckIcon className="shrink-0 w-4 h-4 text-chocolate-600" />
                  VIP support
                  <InfoTooltip label="VIP chat & email support, reach out to the founders directly" />
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function PriceBreakdown({ plan, coupon, billingPeriod }) {
  const monthlyCouponDiscount = getCouponDiscount(plan?.monthlyPrice, {
    amountOff: coupon?.amount_off,
    percentOff: coupon?.percent_off,
  });

  const yearlyCouponDiscount = getCouponDiscount(plan?.monthlyPrice * 10, {
    amountOff: coupon?.amount_off,
    percentOff: coupon?.percent_off,
  });

  const discountedMonthlyPrice = plan?.monthlyPrice - monthlyCouponDiscount;

  const initialYearlyPrice = plan?.monthlyPrice * 12;
  const yearlyPrice = plan?.monthlyPrice * 10;
  const discountedYearlyPrice = yearlyPrice - yearlyCouponDiscount;

  return (
    <div className="mb-2">
      {billingPeriod === 'year' && (
        <>
          <h3 className="text-2xl mt-2 font-medium mb-4 text-black">
            <span className="line-through text-gray-600 mr-2">
              {formatPrice({
                amount: initialYearlyPrice,
                currency: 'USD',
              })}{' '}
              / year
            </span>
            {formatPrice({
              amount: yearlyPrice,
              currency: 'USD',
            })}{' '}
            / year
          </h3>

          {coupon && (
            <>
              <div className="bg-orange-50 px-4 py-4 space-y-1 text-right rounded inline-block">
                <p>
                  <span className="font-semibold">Discount</span>:{' '}
                  {formatPrice({
                    amount: yearlyCouponDiscount,
                    currency: 'USD',
                  })}
                  <>
                    {coupon.duration === 'once' && ' (one-time)'}

                    {coupon.duration === 'forever' && ' / year'}

                    {coupon.duration === 'repeating' && ` (${coupon.duration_in_months} months)`}
                  </>
                </p>

                <p>
                  <span className="font-semibold">Total</span>:{' '}
                  {formatPrice({
                    amount: discountedYearlyPrice,
                    currency: 'USD',
                  })}{' '}
                  / year
                </p>
              </div>
            </>
          )}
        </>
      )}

      {billingPeriod === 'month' && (
        <>
          <h3 className="text-2xl mt-2 font-medium mb-4 text-black">
            {formatPrice({ amount: plan.monthlyPrice, currency: 'USD' })} / month
          </h3>

          {coupon && (
            <div className="bg-orange-50 px-4 py-4 space-y-1 text-right rounded inline-block">
              <p>
                <span className="font-semibold">Discount</span>:{' '}
                {formatPrice({
                  amount: monthlyCouponDiscount,
                  currency: 'USD',
                })}
                <>
                  {coupon.duration === 'once' && ' (one-time)'}

                  {coupon.duration === 'forever' && ' / month'}

                  {coupon.duration === 'repeating' && ` (${coupon.duration_in_months} months)`}
                </>
              </p>

              <p>
                <span className="font-semibold">Total</span>:{' '}
                {formatPrice({
                  amount: discountedMonthlyPrice,
                  currency: 'USD',
                })}{' '}
                / month
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const SubscribePage = () => {
  const { sellerId, token } = useAuth();
  const {
    plan: planHandleQuery,
    billing_period: billingPeriodQuery,
    coupon: couponQuery,
    upgradeFlow: upgradeFlowQuery,
  } = useUrlQuery();

  const [planHandle] = useState(planHandleQuery || 'launch');
  const [billingPeriod, setBillingPeriod] = useState(billingPeriodQuery || 'year');
  const [couponCode] = useState(couponQuery);
  const [upgradeFlow, setUpgradeFlow] = useState(upgradeFlowQuery || 'upgrade-page');

  const { coupon: rewardfulCoupon } = useRewardful();

  const { data: coupon } = useQuery({
    queryKey: ['coupon', couponCode, rewardfulCoupon, sellerId],
    queryFn: () =>
      fetcher(
        `/sellers/${sellerId}/validate-coupon?coupon=${couponCode || rewardfulCoupon.id}`,
        token,
      ),
    enabled: !!sellerId && (!!couponCode || !!rewardfulCoupon),
    retry: false,
  });

  const upgradePlan = getPlan(planHandle);

  function switchBillingPeriodToYearly() {
    setBillingPeriod('year');
    setUpgradeFlow('upgrade-page-yearly-nudge');
  }

  return (
    <Elements stripe={loadStripe(stripePublicKey)} options={{}}>
      <div className="max-w-xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold">Upgrade to {upgradePlan.title}</h1>

          <PriceBreakdown plan={upgradePlan} coupon={coupon} billingPeriod={billingPeriod} />

          <p className="text-black mb-4">Complete your details below to start your subscription</p>

          {billingPeriod === 'month' && (
            <div className="w-full rounded border-dashed border-2 border-chocolate-1000 bg-chocolate-50 p-4 text-lg font-semibold flex items-center justify-between my-6 ">
              <div>
                Get <span className="text-chocolate-1000">2 months for free</span> by switching to
                yearly
              </div>
              <Button
                appearance="primary"
                onClick={switchBillingPeriodToYearly}
                className="ml-auto"
              >
                Accept
              </Button>
            </div>
          )}
        </div>

        <div className="max-w-xl m-auto">
          <UpgradeForm
            onSuccessPath="/"
            planHandle={planHandle}
            billingPeriod={billingPeriod}
            couponQuery={couponQuery}
            upgradeFlow={upgradeFlow}
          />

          <div className="mt-8 text-center">
            <Link
              to={`/upgrade?${stringify({
                plan: planHandle,
                billing_period: billingPeriod,
                coupon: couponQuery,
                upgradeFlow,
              })}`}
              variant="secondary"
            >
              Back
            </Link>
          </div>
        </div>
      </div>
    </Elements>
  );
};

const UpgradePage = () => {
  return (
    <Switch>
      <Route path="/upgrade" exact component={PlansPage} />

      <Route path="/upgrade/subscribe" exact component={SubscribePage} />
    </Switch>
  );
};

export default UpgradePage;
